import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Ban,
  Check,
  ChefHat,
  Clock,
  Download,
  Map,
  MessageCircle,
  MapPin,
  CalendarClock,
  Package,
  PackageCheck,
  Receipt,
  Star,
  Store,
  Bike,
} from 'lucide-react';
import {
  CUSTOMER_CANCELLABLE_STATUSES,
  isTerminalOrder,
  ORDER_STATUS_STEP,
  OrderStatus,
  OrderType,
  ParcelApproval,
  PARCEL_SIZE_LABELS,
  PARCEL_STATUS_STEP,
  PAYMENT_METHOD_LABELS,
  PaymentStatus,
  WS_EVENT,
} from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP, formatDistance, formatFecha, formatHora } from '@/lib/cn';
import { abrirComprobante } from '@/lib/comprobante';
import {
  useAceptarPrecioEncomienda,
  useCancelarPedido,
  usePedido,
  type PedidoDetalle,
} from '@/lib/queries';
import {
  useConexion,
  useEventoSocket,
  useSalaPedido,
  type EstadoConexion,
} from '@/lib/socket';
import { Boton, Cargando, ErrorCarga, Modal } from '@/components/ui';
import { useAvisos } from '@/components/avisos';
import { ListaPedido } from '@/components/ListaPedido';
import { ChatPedido } from '@/components/ChatPedido';
import { EtiquetaEstado } from '@/components/EtiquetaEstado';
import { PagoTransferencia } from '@/components/PagoTransferencia';
import { Calificar } from '@/components/Calificar';
import { Reclamar } from '@/components/Reclamar';

const PASOS = [
  { paso: 1, texto: 'Pedido realizado', icono: Receipt },
  { paso: 2, texto: 'Aceptado por el comercio', icono: Check },
  { paso: 3, texto: 'En preparación', icono: ChefHat },
  { paso: 4, texto: 'Listo para recoger', icono: PackageCheck },
  { paso: 5, texto: 'En camino', icono: Bike },
  { paso: 6, texto: 'Entregado', icono: MapPin },
] as const;

/**
 * La encomienda tiene tres pasos, no seis.
 *
 * Reutilizar la línea del domicilio dejaría «aceptado por el comercio» y «en
 * preparación» apagados para siempre, y una barra que nunca llega al final se
 * lee como que algo va mal.
 */
const PASOS_ENCOMIENDA = [
  { paso: 1, texto: 'Buscando domiciliario', icono: Receipt },
  { paso: 2, texto: 'Paquete recogido', icono: Package },
  { paso: 3, texto: 'Entregado', icono: MapPin },
] as const;

export function Pedido() {
  const { id } = useParams<{ id: string }>();
  const avisos = useAvisos();
  const conexion = useConexion();
  const pedido = usePedido(id, conexion === 'conectado');
  const cancelar = useCancelarPedido();

  const [confirmando, setConfirmando] = useState(false);
  const [motivo, setMotivo] = useState('');

  useSeguimientoEnVivo(id, conexion);

  if (pedido.isLoading) return <Cargando texto="Cargando tu pedido…" />;
  if (pedido.isError || !pedido.data) {
    return (
      <div className="mx-auto max-w-2xl py-8">
        <ErrorCarga
          mensaje={getErrorMessage(pedido.error)}
          onReintentar={() => void pedido.refetch()}
        />
      </div>
    );
  }

  const p = pedido.data;
  const encomienda = p.type === OrderType.PARCEL;
  const pasos = encomienda ? PASOS_ENCOMIENDA : PASOS;
  const pasoActual = encomienda ? PARCEL_STATUS_STEP[p.status] : ORDER_STATUS_STEP[p.status];
  const cancelado = p.status === OrderStatus.CANCELLED || p.status === OrderStatus.REJECTED;
  const puedeCancelar = CUSTOMER_CANCELLABLE_STATUSES.includes(p.status);

  const confirmarCancelacion = (): void => {
    cancelar.mutate(
      { id: p.id, reason: motivo.trim() || 'El cliente canceló el pedido' },
      {
        onSuccess: () => {
          setConfirmando(false);
          avisos.exito('Pedido cancelado');
        },
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  return (
    <div className="mx-auto max-w-2xl pb-8">
      <Link
        to="/pedidos"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-kick-gray transition hover:text-kick-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Mis pedidos
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">{p.code}</h1>
          <p className="mt-1 text-sm text-kick-gray">
            {p.merchantName ?? 'Encomienda'} · {formatFecha(p.createdAt)}
          </p>
        </div>
        <EtiquetaEstado estado={p.status} className="mt-2" />
      </div>

      {p.parcel?.scheduledFor && <Programada pedido={p} />}

      {/* Seguimiento */}
      <section className="card-kick mt-6 p-5">
        {cancelado ? (
          <div className="flex items-start gap-3 rounded-kick bg-red-50 p-4">
            <Ban className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div>
              <p className="font-semibold text-red-800">
                {p.status === OrderStatus.REJECTED
                  ? 'El comercio rechazó el pedido'
                  : 'Pedido cancelado'}
              </p>
              {p.cancelReason && <p className="mt-0.5 text-sm text-red-700">{p.cancelReason}</p>}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg text-kick-dark">Seguimiento</h2>
              {p.estimatedDeliveryAt && p.status !== OrderStatus.DELIVERED && (
                <span className="flex items-center gap-1.5 text-sm text-kick-gray">
                  <Clock className="h-4 w-4" />
                  Llega ~{formatHora(p.estimatedDeliveryAt)}
                </span>
              )}
            </div>

            <ol className="mt-4">
              {pasos.map(({ paso, texto, icono: Icono }, indice) => {
                const hecho = pasoActual >= paso;
                const actual = pasoActual === paso;
                return (
                  <li key={paso} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'grid h-8 w-8 shrink-0 place-items-center rounded-full transition',
                          hecho ? 'bg-kick-red text-white' : 'bg-kick-light text-kick-gray/50',
                          actual && 'ring-4 ring-kick-red/20',
                        )}
                      >
                        <Icono className="h-4 w-4" />
                      </span>
                      {indice < pasos.length - 1 && (
                        <span
                          className={cn(
                            'w-0.5 flex-1',
                            pasoActual > paso ? 'bg-kick-red' : 'bg-kick-gray/20',
                          )}
                        />
                      )}
                    </div>
                    <div className={cn('pb-5', indice === pasos.length - 1 && 'pb-0')}>
                      <p
                        className={cn(
                          'font-semibold',
                          hecho ? 'text-kick-ink' : 'text-kick-gray/60',
                        )}
                      >
                        {texto}
                      </p>
                      <p className="text-sm text-kick-gray">
                        {marcaDePaso(p.history, paso) ?? (actual ? 'En este momento' : '')}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>

            {/* Quién lo lleva, en cuanto el motor de asignación lo encuentra. */}
            {p.courier && (
              <div className="mt-3 flex items-center gap-3 rounded-kick bg-kick-light p-3">
                {p.courier.avatarUrl ? (
                  <img
                    src={p.courier.avatarUrl}
                    alt=""
                    className="h-11 w-11 rounded-full object-cover"
                  />
                ) : (
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-kick-red/10 text-kick-red">
                    <Bike className="h-5 w-5" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-kick-ink">{p.courier.fullName}</p>
                  <p className="text-sm text-kick-gray">
                    {p.courier.plate ? `${p.courier.plate} · ` : ''}
                    {p.courier.maskedPhone}
                  </p>
                </div>
                {p.courier.ratingAvg > 0 && (
                  <span className="flex items-center gap-1 text-sm text-kick-gray">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    {p.courier.ratingAvg.toFixed(1)}
                  </span>
                )}
              </div>
            )}

            {/*
              El chat abre en cuanto hay domiciliario, no cuando sale en camino.
              El domiciliario escribe desde antes de recoger («voy por él») y
              el cliente recibía el aviso sin tener dónde contestar.
            */}
            {p.courier && chatDisponible(p.status) && (
              <BotonChat orderId={p.id} nombre={p.courier.fullName} />
            )}

            {/* El mapa solo tiene sentido cuando hay alguien moviéndose. */}
            {(p.status === OrderStatus.PICKED_UP || p.status === OrderStatus.ON_THE_WAY) && (
              <Link to={`/pedidos/${p.id}/mapa`} className="mt-3 block">
                <Boton className="w-full" icono={<Map className="h-4 w-4" />}>
                  Ver en el mapa
                </Boton>
              </Link>
            )}

            {p.status === OrderStatus.DELIVERED && p.pickedUpAt && (
              <Link to={`/pedidos/${p.id}/mapa`} className="mt-3 block">
                <Boton variante="secundario" className="w-full" icono={<Map className="h-4 w-4" />}>
                  Ver el recorrido
                </Boton>
              </Link>
            )}
          </>
        )}
      </section>

      {p.status === OrderStatus.DELIVERED && (
        <Calificar
          orderId={p.id}
          comercio={p.merchantName}
          hayDomiciliario={!!p.courier}
        />
      )}

      {/* Solo con el pedido terminado: mientras va en camino lo que sirve es el mapa. */}
      {(p.status === OrderStatus.DELIVERED ||
        p.status === OrderStatus.CANCELLED ||
        p.status === OrderStatus.REJECTED) && <Reclamar orderId={p.id} />}

      {/* Entrega */}
      <section className="card-kick mt-4 p-5">
        <h2 className="text-lg text-kick-dark">Entrega</h2>
        <div className="mt-3 flex items-start gap-3">
          <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-kick-gray" />
          <div>
            <p className="font-semibold text-kick-ink">{p.deliveryAddress.label}</p>
            <p className="text-sm text-kick-gray">{p.deliveryAddress.addressLine}</p>
            {p.deliveryAddress.details && (
              <p className="text-sm text-kick-gray">{p.deliveryAddress.details}</p>
            )}
            <p className="mt-1 text-sm text-kick-gray">
              {formatDistance(p.distanceMeters)} desde {p.merchantName ?? p.pickupAddress.addressLine}
            </p>
          </div>
        </div>

        {p.customerNotes && (
          <p className="mt-3 rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">
            <span className="font-semibold">Tus notas: </span>
            {p.customerNotes}
          </p>
        )}
      </section>

      <ListaPedido pedido={p} />

      {/* Detalle */}
      <section className="card-kick mt-4 p-5">
        <h2 className="flex items-center gap-2 text-lg text-kick-dark">
          {encomienda ? (
            <Package className="h-4 w-4 text-kick-gray" />
          ) : (
            <Store className="h-4 w-4 text-kick-gray" />
          )}
          {p.merchantName ?? 'Encomienda'}
        </h2>

        {/*
          Una encomienda no tiene productos: lo que hay que ver es qué se mandó,
          a quién y desde dónde. Una lista de ítems vacía dejaría la tarjeta
          diciendo nada.
        */}
        {p.parcel && (
          <dl className="mt-3 space-y-1.5 border-b border-kick-gray/15 pb-3 text-sm">
            <Fila etiqueta="Qué" valor={p.parcel.description} />
            <Fila etiqueta="Tamaño" valor={PARCEL_SIZE_LABELS[p.parcel.size]} />
            <Fila etiqueta="Se recoge en" valor={p.pickupAddress.addressLine} />
            <Fila etiqueta="Lo recibe" valor={p.parcel.recipientName} />
          </dl>
        )}

        <ul className={cn('mt-3 space-y-2', !encomienda && 'border-b border-kick-gray/15 pb-3')}>
          {p.items.map((item) => (
            <li key={item.id} className="flex gap-2 text-sm">
              <span className="font-semibold text-kick-gray">{item.quantity}×</span>
              <span className="min-w-0 flex-1">
                <span className="block text-kick-ink">{item.productName}</span>
                {item.options.length > 0 && (
                  <span className="block text-xs text-kick-gray">
                    {item.options.map((o) => o.optionName).join(' · ')}
                  </span>
                )}
                {item.notes && (
                  <span className="block text-xs italic text-kick-gray">«{item.notes}»</span>
                )}
              </span>
              <span className="font-semibold text-kick-ink">{formatCOP(item.lineTotal)}</span>
            </li>
          ))}
        </ul>

        <dl className="mt-3 space-y-1.5 text-sm">
          {/* Sin productos no hay subtotal ni tarifa de servicio que mostrar. */}
          {!encomienda && <Fila etiqueta="Subtotal" valor={formatCOP(p.subtotal)} />}
          <Fila
            etiqueta={encomienda ? 'Envío' : 'Domicilio'}
            valor={p.deliveryFee === 0 ? 'Gratis' : formatCOP(p.deliveryFee)}
          />
          {!encomienda && p.serviceFee > 0 && (
            <Fila etiqueta="Tarifa lluvia" valor={formatCOP(p.serviceFee)} />
          )}
          {/* En una encomienda la lluvia va aparte y serviceFee es el recargo no aliado. */}
          {encomienda && p.serviceFee > 0 && (
            <Fila etiqueta="Recargo no aliado" valor={formatCOP(p.serviceFee)} />
          )}
          {encomienda && p.rainFee > 0 && (
            <Fila etiqueta="Tarifa lluvia" valor={formatCOP(p.rainFee)} />
          )}
          {p.tip > 0 && <Fila etiqueta="Propina" valor={formatCOP(p.tip)} />}
          {p.discountTotal > 0 && (
            <Fila etiqueta="Descuento" valor={`− ${formatCOP(p.discountTotal)}`} />
          )}
        </dl>

        <div className="mt-3 flex items-baseline justify-between border-t border-kick-gray/15 pt-3">
          <span className="font-semibold text-kick-ink">Total</span>
          <span className="text-2xl font-bold text-kick-dark">{formatCOP(p.total)}</span>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-kick-gray">
            {PAYMENT_METHOD_LABELS[p.paymentMethod]} ·{' '}
            {p.paymentStatus === PaymentStatus.PAID ? 'Pagado' : 'Pendiente de pago'}
          </p>
          <button
            type="button"
            onClick={() => abrirComprobante(p)}
            className="flex items-center gap-1.5 text-sm font-semibold text-kick-red hover:underline"
          >
            <Download className="h-4 w-4" />
            Comprobante
          </button>
        </div>
      </section>

      {/* Solo aparece si el pedido se paga por transferencia. */}
      <PagoTransferencia orderId={p.id} total={p.total} />

      {puedeCancelar && (
        <Boton variante="peligro" className="mt-4 w-full" onClick={() => setConfirmando(true)}>
          Cancelar pedido
        </Boton>
      )}

      {isTerminalOrder(p.status) && (
        <Link to={`/c/${p.merchantSlug}`} className="mt-4 block">
          <Boton variante="secundario" className="w-full">
            Volver a pedir
          </Boton>
        </Link>
      )}

      <Modal
        abierto={confirmando}
        onCerrar={() => setConfirmando(false)}
        titulo="Cancelar el pedido"
        descripcion="Solo puedes cancelar mientras el comercio no haya empezado a prepararlo."
        ancho="sm"
      >
        <div className="p-5">
          <label htmlFor="motivo" className="mb-1.5 block text-sm font-semibold text-kick-ink">
            ¿Por qué lo cancelas?
          </label>
          <input
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Me equivoqué de dirección"
            maxLength={200}
            className="w-full rounded-kick border border-kick-gray/30 bg-white px-3.5 py-2.5 text-kick-ink outline-none transition focus:border-kick-red focus:ring-2 focus:ring-kick-red/20"
          />
          <div className="mt-6 flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => setConfirmando(false)}>
              Volver
            </Boton>
            <Boton
              variante="peligro"
              cargando={cancelar.isPending}
              onClick={confirmarCancelacion}
            >
              Cancelar pedido
            </Boton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Mantiene el seguimiento al día con los eventos del servidor.
 *
 * Se entra a la sala del pedido y cada evento invalida la consulta: el detalle
 * completo llega del servidor en vez de reconstruirse a trozos en el
 * navegador. Al reconectar se invalida también, porque los cambios ocurridos
 * mientras el socket estuvo caído no llegaron por ningún lado.
 */
function useSeguimientoEnVivo(orderId: string | undefined, conexion: EstadoConexion): void {
  const qc = useQueryClient();
  useSalaPedido(orderId);

  const refrescar = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['pedido', orderId] });
    void qc.invalidateQueries({ queryKey: ['mis-pedidos'] });
    // El pago cambia con el pedido: al asignarse el domiciliario aparecen sus
    // cuentas, y al revisarse el comprobante cambia lo que falta por hacer.
    void qc.invalidateQueries({ queryKey: ['pago', orderId] });
  }, [qc, orderId]);

  useEventoSocket(WS_EVENT.ORDER_STATUS_CHANGED, refrescar);
  useEventoSocket(WS_EVENT.ORDER_ETA_UPDATED, refrescar);
  useEventoSocket(WS_EVENT.COURIER_ASSIGNED, refrescar);
  useEventoSocket(WS_EVENT.PAYMENT_RESOLVED, refrescar);

  useEffect(() => {
    if (conexion === 'conectado') refrescar();
  }, [conexion, refrescar]);
}

function Fila({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-kick-gray">{etiqueta}</dt>
      <dd className="text-kick-ink">{valor}</dd>
    </div>
  );
}

/**
 * Hora en que el pedido alcanzó ese paso.
 *
 * `PICKED_UP` y `ON_THE_WAY` comparten el paso 5: se toma la primera de las
 * dos, que es cuando el domiciliario salió con el pedido.
 */
function marcaDePaso(
  historial: { toStatus: OrderStatus; createdAt: string }[],
  paso: number,
): string | null {
  const entrada = historial.find((h) => ORDER_STATUS_STEP[h.toStatus] === paso);
  return entrada ? formatHora(entrada.createdAt) : null;
}

/**
 * En qué va una encomienda programada.
 *
 * Ocupa el sitio de la línea de tiempo porque hasta que salga no hay línea de
 * tiempo que enseñar: lo que la persona necesita saber es si se la aprobaron,
 * para cuándo y si le están pidiendo algo.
 */
function Programada({ pedido }: { pedido: PedidoDetalle }) {
  const parcel = pedido.parcel!;
  const aceptar = useAceptarPrecioEncomienda(pedido.id);
  const avisos = useAvisos();

  const cuando = new Date(parcel.scheduledFor!).toLocaleString('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  });

  if (parcel.approval === ParcelApproval.AWAITING_REPRICE) {
    return (
      <section className="card-kick mt-6 border-l-4 border-amber-400 p-5">
        <h2 className="flex items-center gap-2 text-lg text-kick-dark">
          <CalendarClock className="h-5 w-5 text-amber-600" />
          Confirma el precio para que salga
        </h2>
        <p className="mt-2 text-sm text-kick-gray">
          La tarifa cambió desde que la reservaste. Ahora cuesta{' '}
          <strong className="text-kick-ink">{formatCOP(parcel.repricedTotal ?? pedido.total)}</strong>{' '}
          en vez de {formatCOP(parcel.quotedTotal ?? pedido.total)}. Si no confirmas pronto la
          cancelamos y no se cobra nada.
        </p>
        <Boton
          className="mt-4"
          cargando={aceptar.isPending}
          onClick={() =>
            aceptar.mutate(undefined, {
              onSuccess: () => avisos.exito('Listo, vamos por tu paquete'),
              onError: (e) => avisos.error(getErrorMessage(e)),
            })
          }
        >
          Acepto {formatCOP(parcel.repricedTotal ?? pedido.total)}
        </Boton>
      </section>
    );
  }

  const rechazada = parcel.approval === ParcelApproval.REJECTED;
  const aprobada = parcel.approval === ParcelApproval.APPROVED;

  return (
    <section
      className={cn(
        'card-kick mt-6 border-l-4 p-5',
        rechazada ? 'border-red-400' : aprobada ? 'border-emerald-400' : 'border-amber-400',
      )}
    >
      <h2 className="flex items-center gap-2 text-lg text-kick-dark">
        <CalendarClock
          className={cn(
            'h-5 w-5',
            rechazada ? 'text-red-600' : aprobada ? 'text-emerald-600' : 'text-amber-600',
          )}
        />
        {rechazada ? 'No pudimos programarla' : `Recogemos el ${cuando}`}
      </h2>

      <p className="mt-2 text-sm text-kick-gray">
        {rechazada
          ? (parcel.decisionNote ?? 'Escríbenos a soporte y lo miramos.')
          : aprobada
            ? 'Confirmada. Ese día buscamos domiciliario y te avisamos cuando vaya en camino.'
            : 'La estamos revisando. Te avisamos por notificación en cuanto la confirmemos.'}
      </p>
    </section>
  );
}

/** Mientras el pedido viva y tenga domiciliario se puede conversar; el reclamo va por su sitio. */
function chatDisponible(estado: OrderStatus): boolean {
  return (
    estado !== OrderStatus.DELIVERED &&
    estado !== OrderStatus.CANCELLED &&
    estado !== OrderStatus.REJECTED
  );
}

function BotonChat({ orderId, nombre }: { orderId: string; nombre: string }) {
  const [abierto, setAbierto] = useState(false);

  return (
    <>
      <Boton
        variante="secundario"
        className="mt-3 w-full"
        icono={<MessageCircle className="h-4 w-4" />}
        onClick={() => setAbierto(true)}
      >
        Escribirle a {nombre.split(' ')[0]}
      </Boton>
      <ChatPedido
        orderId={orderId}
        nombreDomiciliario={nombre}
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
      />
    </>
  );
}
